/**
 * VendorShopService
 * Service for public shop endpoints in VendorManagement (read-only)
 * Route: api/shop
 * Controller: ShopController.cs (CCMS.Api.VendorManagement)
 */
import { BaseService } from '../base/BaseService';
/**
 * VendorShopService
 * Public read-only endpoints for shop vendor and product data.
 * All data is served from Redis cache on the backend.
 */
export class VendorShopService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? VendorShopService.DEFAULT_SERVICE_PATH,
            basePath: VendorShopService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== VENDOR ENDPOINTS ====================
    /**
     * Get all active/approved vendors
     * GET /api/shop/vendors
     */
    async getVendors() {
        const response = await this.getVendorsRaw();
        return response.data ?? [];
    }
    /**
     * Get all vendors (raw response)
     */
    getVendorsRaw() {
        const endpoint = this.getEndpoint('vendors', 'vendors');
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get a single vendor by ID with social media links and products
     * GET /api/shop/vendors/{id}
     */
    async getVendorById(id) {
        const response = await this.getVendorByIdRaw(id);
        if (!response.data) {
            throw new Error(`Vendor ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get vendor by ID (raw response)
     */
    getVendorByIdRaw(id) {
        const endpoint = this.getEndpoint('vendor-by-id', `vendors/${id}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get a vendor's shop view for a specific event
     * Only returns data if the vendor is confirmed for the event
     * GET /api/shop/{eventId}/view/{vendorId}
     */
    async getVendorForEvent(eventId, vendorId) {
        const response = await this.getVendorForEventRaw(eventId, vendorId);
        if (!response.data) {
            throw new Error(`Vendor ${vendorId} not found for event ${eventId}`);
        }
        return response.data;
    }
    /**
     * Get vendor for event (raw response)
     */
    getVendorForEventRaw(eventId, vendorId) {
        const endpoint = this.getEndpoint('vendor-for-event', `${eventId}/view/${vendorId}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get all confirmed vendors for a specific event
     * GET /api/shop/events/{eventId}/vendors
     */
    async getVendorsForEvent(eventId) {
        const response = await this.getVendorsForEventRaw(eventId);
        return response.data ?? [];
    }
    /**
     * Get vendors for event (raw response)
     */
    getVendorsForEventRaw(eventId) {
        const endpoint = this.getEndpoint('vendors-for-event', `events/${eventId}/vendors`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get active vendors filtered by type
     * GET /api/shop/vendors/by-type/{typeId}
     */
    async getVendorsByType(typeId) {
        const response = await this.getVendorsByTypeRaw(typeId);
        return response.data ?? [];
    }
    /**
     * Get vendors by type (raw response)
     */
    getVendorsByTypeRaw(typeId) {
        const endpoint = this.getEndpoint('vendors-by-type', `vendors/by-type/${typeId}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    // ==================== PRODUCT ENDPOINTS ====================
    /**
     * Get all active products
     * GET /api/shop/products
     */
    async getProducts() {
        const response = await this.getProductsRaw();
        return response.data ?? [];
    }
    /**
     * Get all products (raw response)
     */
    getProductsRaw() {
        const endpoint = this.getEndpoint('products', 'products');
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get a single product by ID with media and variants
     * GET /api/shop/products/{id}
     */
    async getProductById(id) {
        const response = await this.getProductByIdRaw(id);
        if (!response.data) {
            throw new Error(`Product ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get product by ID (raw response)
     */
    getProductByIdRaw(id) {
        const endpoint = this.getEndpoint('product-by-id', `products/${id}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get active products for a specific vendor
     * GET /api/shop/products/vendor/{vendorId}
     */
    async getProductsByVendor(vendorId) {
        const response = await this.getProductsByVendorRaw(vendorId);
        return response.data ?? [];
    }
    /**
     * Get products by vendor (raw response)
     */
    getProductsByVendorRaw(vendorId) {
        const endpoint = this.getEndpoint('products-by-vendor', `products/vendor/${vendorId}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get featured active products
     * GET /api/shop/products/featured
     */
    async getFeaturedProducts() {
        const response = await this.getFeaturedProductsRaw();
        return response.data ?? [];
    }
    /**
     * Get featured products (raw response)
     */
    getFeaturedProductsRaw() {
        const endpoint = this.getEndpoint('products-featured', 'products/featured');
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
}
VendorShopService.DEFAULT_SERVICE_PATH = 'vendors';
VendorShopService.BASE_PATH = 'api/shop';
//# sourceMappingURL=VendorShopService.js.map